import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Cigarette, Refrigerator, Coffee, Search, X, Moon, Layers } from "lucide-react";
import { QUICK_SELL_SOURCES, boxNoun, sourceOf } from "../lib/cafeSources";
import { formatMoney } from "../lib/format";
import { nightIncreaseAt, priceNow } from "../lib/nightPricing";
import { describeStock, stockState, stockTone } from "../lib/stock";
import { isFrameGame } from "../lib/frames";
import { useMinuteTick } from "../hooks/useMinuteTick";
import QuantityDialog from "./QuantityDialog";

const SOURCE_ICONS = { cigarettes: Cigarette, fridge: Refrigerator };

// Cigarettes and drinks first (the everyday buys at a table), then the menu.
const SECTIONS = [
  ...QUICK_SELL_SOURCES.map((s) => ({
    source: s.source, title: s.title, accent: s.accent,
    packNoun: s.packNoun, packNounBox: s.packNounBox,
  })),
  {
    source: "cafe",
    title: "Menu",
    accent: {
      icon: "text-amber-400",
      price: "text-amber-400",
      hover: "hover:border-amber-500/50 hover:bg-amber-500/5 active:bg-amber-500/15",
    },
  },
];

/**
 * Put items on a running table's tab. One tap adds one unit; the dialog stays
 * open so several items go on in a row, and each tile shows how many are
 * already on the tab. Items are fetched when the dialog opens — nothing loads
 * until someone actually wants to add something.
 *
 * `onAdd(itemId, quantity)` sends the item somewhere other than the table's
 * tab: the waiting list passes it, with the waiting person as the `table`.
 */
export default function AddToTabDialog({ open, onOpenChange, table, forSide = null, onAdd = null }) {
  const { apiClient, addToTab, settings } = useApp();
  const addItem = (itemId, quantity, playerId) => (
    onAdd ? onAdd(itemId, quantity) : addToTab(table.id, itemId, quantity, playerId)
  );
  // Tiles show the price that will be charged, switching at night on their own.
  const now = useMinuteTick();
  const night = settings?.night_pricing;
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState("");
  const [addingId, setAddingId] = useState(null);
  // Item whose "Bulk" button was tapped — a round of drinks goes on in one go
  const [bulkItem, setBulkItem] = useState(null);
  const [addingBulk, setAddingBulk] = useState(false);
  // On a loser-pays table, whose name the next item goes on. null = the table's.
  const [forPlayer, setForPlayer] = useState(null);

  const session = table.active_session;
  const allPlayers = isFrameGame(session) ? session.players || [] : [];
  // Opened from a player or team row: only that side's names are offered.
  const players = forSide
    ? allPlayers.filter((p) => (forSide.member_ids || []).includes(p.id))
    : allPlayers;

  // A stable key for "which names are on offer", so the effects below do not
  // re-run on every render just because the array was rebuilt.
  const sideKey = players.map((p) => p.id).join(",");

  // One name on this side (singles, or a row tapped for one player) is already
  // decided. Otherwise nothing is chosen until someone taps.
  useEffect(() => {
    if (!open) return;
    setForPlayer(players.length === 1 ? players[0] : null);
    // players is derived from sideKey; depending on the array itself would
    // reset the choice on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sideKey]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    let cancelled = false;
    setLoadingItems(true);
    apiClient.get("/cafe/items")
      .then((res) => { if (!cancelled) setItems(res.data.items); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load items"); })
      .finally(() => { if (!cancelled) setLoadingItems(false); });
    return () => { cancelled = true; };
  }, [open, apiClient]);

  const tab = table.active_session?.orders || [];
  const countOnTab = (itemId) => tab.reduce((n, o) => n + (o.item_id === itemId ? o.quantity : 0), 0);

  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (item) => {
    if (terms.length === 0) return true;
    const haystack = `${item.name || ""} ${item.category || ""} ${item.selling_price}`.toLowerCase();
    return terms.every((t) => haystack.includes(t));
  };

  // Nothing chosen yet — an item tapped now would land on nobody by default.
  const needsName = players.length > 0 && !forPlayer;

  const handleAdd = async (item) => {
    if (addingId) return; // one request at a time guards against a double tap
    if (needsName) {
      toast.error("Tap whose it is first — or choose The table");
      return;
    }
    if (stockState(item).out) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setAddingId(item.id);
    try {
      const onName = forPlayer && forPlayer.id !== "table" ? forPlayer : null;
      await addItem(item.id, 1, onName?.id || null);
      toast.success(`${item.name} — ${onName ? onName.name : table.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add item");
    }
    setAddingId(null);
  };

  const handleAddBulk = async (quantity) => {
    if (!bulkItem) return;
    if (needsName) {
      toast.error("Tap whose it is first — or choose The table");
      return;
    }
    setAddingBulk(true);
    try {
      const onName = forPlayer && forPlayer.id !== "table" ? forPlayer : null;
      await addItem(bulkItem.id, quantity, onName?.id || null);
      toast.success(`${quantity} × ${bulkItem.name} — ${onName ? onName.name : table.name}`);
      setBulkItem(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add item");
    }
    setAddingBulk(false);
  };

  const sections = SECTIONS
    .map((s) => ({ ...s, items: items.filter((i) => sourceOf(i) === s.source && matches(i)) }))
    .filter((s) => s.items.length > 0);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-2xl max-h-[85vh] flex flex-col"
        data-testid={`add-to-tab-dialog-${table.id}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Add to {forSide ? forSide.name : table.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {players.length > 0
              ? "Pick whose it is first, then tap the item. Paid with the game."
              : "Tap once per item, or \"Bulk\" for several. Items are paid with the game."}
          </DialogDescription>
        </DialogHeader>

        {players.length > 0 && (
          <div data-testid={`tab-player-picker-${table.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
              Put it on{" "}
              {forPlayer
                ? <span className="text-green-400">· {forPlayer.name}</span>
                : <span className="text-amber-400">· tap a name</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[{ id: "table", name: "The table" }, ...players].map((p) => {
                const active = forPlayer?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`tab-for-${p.id}`}
                    onClick={() => setForPlayer(p)}
                    className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors duration-150 ${
                      active
                        ? "bg-green-600 text-white"
                        : "bg-[#09090b] border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                    }`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <Input
            data-testid="add-to-tab-search"
            placeholder="Search cigarettes, drinks, menu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9 h-10 bg-[#09090b] border-zinc-700 text-zinc-200 placeholder:text-zinc-500"
          />
          {search && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1 -mr-1">
          {loadingItems ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sections.length === 0 ? (
            <p className="text-center text-zinc-500 py-10">
              {items.length === 0 ? "No items set up yet — add them in Settings and on the Cafe page." : `Nothing matches "${search.trim()}"`}
            </p>
          ) : (
            sections.map((s) => {
              const Icon = SOURCE_ICONS[s.source] || Coffee;
              const nightUp = nightIncreaseAt(night, s.source, now);
              return (
                <div key={s.source}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${s.accent.icon}`} />
                    <h3 className="font-heading text-lg font-semibold text-zinc-100">{s.title}</h3>
                    {nightUp > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300">
                        <Moon className="w-3 h-3" /> Night +₹{formatMoney(nightUp)}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {s.items.map((item) => {
                      const count = countOnTab(item.id);
                      return (
                        // Tap = one unit, as before. "Bulk" is its own button
                        // beside it, so a round for the whole table is one go.
                        <div key={item.id} className="relative">
                          <button
                            type="button"
                            data-testid={`add-to-tab-${item.id}`}
                            onClick={() => handleAdd(item)}
                            disabled={addingId === item.id || stockState(item).out}
                            className={`w-full bg-[#09090b] border rounded-lg p-3 text-left transition-colors duration-150 ${
                              stockState(item).out
                                ? "border-zinc-800/60 opacity-50 grayscale cursor-not-allowed"
                                : `border-zinc-800 disabled:opacity-60 ${s.accent.hover}`
                            }`}
                          >
                            {count > 0 && (
                              <span className="absolute top-2 right-2 min-w-5 h-5 px-1.5 rounded-full bg-green-600 text-white text-[11px] font-bold flex items-center justify-center">
                                {count}
                              </span>
                            )}
                            <p className="font-medium text-zinc-100 truncate pr-6">{item.name}</p>
                            <p className={`${s.accent.price} text-sm font-semibold mt-0.5 pr-9 sm:pr-14`}>₹{formatMoney(priceNow(item, night, now))}</p>
                            {stockState(item).tracked && (
                              <p className={`text-[10px] uppercase tracking-wider mt-0.5 pr-9 sm:pr-14 ${stockTone(stockState(item))}`}>
                                {describeStock(stockState(item), boxNoun(s, item.pack_size))}
                              </p>
                            )}
                          </button>
                          <button
                            type="button"
                            aria-label={`Add several ${item.name}`}
                            data-testid={`add-to-tab-bulk-${item.id}`}
                            onClick={() => setBulkItem(item)}
                            disabled={stockState(item).out}
                            className="absolute bottom-2 right-2 disabled:opacity-40 disabled:cursor-not-allowed h-6 w-6 sm:w-auto sm:pl-1.5 sm:pr-2 rounded-md border border-zinc-700 bg-[#18181b] text-zinc-400 text-[10px] font-semibold uppercase tracking-wider flex items-center justify-center gap-1 hover:text-zinc-100 hover:border-zinc-500 transition-colors duration-150"
                          >
                            <Layers className="w-3 h-3" /> <span className="hidden sm:inline">Bulk</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button
            data-testid="add-to-tab-done"
            onClick={() => onOpenChange(false)}
            className="w-full h-11 font-heading font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Stacks on top of the picker, which stays open behind it so the next
        item can go on straight after. */}
    <QuantityDialog
      open={!!bulkItem}
      onOpenChange={(next) => { if (!next) setBulkItem(null); }}
      item={bulkItem}
      unitPrice={bulkItem ? priceNow(bulkItem, night, now) : 0}
      title={bulkItem ? `Add ${bulkItem.name}` : ""}
      description={`Goes on ${table.name}'s bill, paid when the game stops.`}
      confirmLabel="Add to tab"
      busyLabel="Adding..."
      busy={addingBulk}
      onConfirm={handleAddBulk}
    />
    </>
  );
}
